import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import axios from 'axios';

import {
    useNavigate,
    Link
} from 'react-router-dom';

import {
    Layout,
    Row,
    Col,
    Card,
    Typography,
    Spin,
    Avatar,
    Space,
    Divider,
    Button,
    Statistic,
    Progress,
    Tag,
    Alert,
    Table,
    DatePicker
} from 'antd';

import {
    ProjectOutlined,
    FileTextOutlined,
    OrderedListOutlined,
    DollarCircleOutlined,
    TeamOutlined,
    LayoutOutlined,
    BuildOutlined,
    TagsOutlined,
    EditOutlined,
    UserOutlined,
    LockOutlined,
    SolutionOutlined,
    CalendarOutlined,
    LogoutOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CalculatorOutlined,
    RiseOutlined,
    ReloadOutlined,
    CarOutlined,
    HistoryOutlined,
    SettingOutlined,
    AppstoreOutlined,
    ControlOutlined,
    FlagOutlined,
    MessageOutlined,
    SafetyCertificateOutlined,
    InboxOutlined,
    FileSearchOutlined,
    ApartmentOutlined,
    PartitionOutlined,
    BankOutlined,
    SearchOutlined,
    ContactsOutlined,
    AudioOutlined
} from '@ant-design/icons';

import {
    ResponsiveContainer,
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip as ChartTooltip,
    Legend,
    PieChart,
    Pie,
    Cell
} from 'recharts';

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
    canAtLeast,
    getCachedPageAccess,
    loadMyPageAccess,
    pathToPageKey
} from '../utils/pageAccess';

dayjs.extend(isoWeek);

const {
    Content
} = Layout;

const {
    Title,
    Text
} = Typography;

const {
    RangePicker
} = DatePicker;

/*
 * Цвета используются только внутри диаграммы.
 */
const ESTIMATE_COLORS = [
    '#ff4d4f',
    '#faad14',
    '#52c41a'
];

const DOCUMENT_COLORS = [
    '#1677ff',
    '#722ed1',
    '#13c2c2',
    '#fa8c16'
];

const TASK_STAGE_COLORS = [
    '#1677ff',
    '#722ed1',
    '#13c2c2',
    '#52c41a',
    '#faad14',
    '#fa8c16',
    '#eb2f96',
    '#2f54eb',
    '#a0d911',
    '#f5222d',
    '#8c8c8c',
    '#08979c'
];

const Dashboard = () => {
    const [
        user,
        setUser
    ] = useState(null);

    const [
        statistics,
        setStatistics
    ] = useState(null);

    const [
        loading,
        setLoading
    ] = useState(true);
    
    const [
        statisticsLoading,
        setStatisticsLoading
    ] = useState(false);

    const [
        statisticsPeriod,
        setStatisticsPeriod
    ] = useState([
        dayjs().startOf('day'),
        dayjs().endOf('day')
    ]);

    const [
        exitPeriod,
        setExitPeriod
    ] = useState(null);

    const [
        bpClosingSummary,
        setBpClosingSummary
    ] = useState(null);

    const [
        estimateRankingPeriod,
        setEstimateRankingPeriod
    ] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);

    const [
        estimateRankingRows,
        setEstimateRankingRows
    ] = useState([]);

    const [
        estimateRankingLoading,
        setEstimateRankingLoading
    ] = useState(false);

    const [
        estimateRankingByCustomer,
        setEstimateRankingByCustomer
    ] = useState([]);

    const [
        calendarEvents,
        setCalendarEvents
    ] = useState([]);

    const navigate =
        useNavigate();

    const [accessTick, setAccessTick] = useState(0);

    useEffect(() => {
        const refresh = () => setAccessTick(tick => tick + 1);
        window.addEventListener('page-access', refresh);
        loadMyPageAccess().catch(() => {});
        return () => window.removeEventListener('page-access', refresh);
    }, []);

    /*
    * Определяем роль пользователя так же,
    * как это сделано во всем приложении.
    */
    const currentUser = (() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    })();

    const currentRoleId = Number(
        currentUser?.role_id ??
        currentUser?.roleId ??
        localStorage.getItem('roleId')
    );

    /*
    * Только администратор (роль 1)
    * видит расширенную статистику.
    */
    const isAdmin = currentRoleId === 1;
    const isManager = currentRoleId === 2;
    const canSeeEstimateRanking =
        isAdmin || isManager;
    const canSeeCalendar =
        isAdmin ||
        canAtLeast(getCachedPageAccess()?.calendar, 'view');

    const fetchDashboard = async (
        period = statisticsPeriod,
        initial = false,
        selectedExitPeriod = exitPeriod
    ) => {
    const token =
        localStorage.getItem(
            'token'
        );

    if (!token) {
        navigate('/login');
        return;
    }

    const safePeriod =
        period?.[0] &&
        period?.[1]
            ? period
            : [
                dayjs().startOf('day'),
                dayjs().endOf('day')
            ];

    if (initial) {
        setLoading(true);
    } else {
        setStatisticsLoading(true);
    }

    try {
        const response =
            await axios.get(
                '/api/dashboard',
                {
                    params: {
    date_from:
        safePeriod[0].format(
            'YYYY-MM-DD'
        ),

    date_to:
        safePeriod[1].format(
            'YYYY-MM-DD'
        ),

    exit_date_from:
        selectedExitPeriod?.[0]
            ? selectedExitPeriod[0]
                .format(
                    'YYYY-MM-DD'
                )
            : undefined,

    exit_date_to:
        selectedExitPeriod?.[1]
            ? selectedExitPeriod[1]
                .format(
                    'YYYY-MM-DD'
                )
            : undefined
},

                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    },

                    withCredentials:
                        true
                }
            );

        setUser(
            response.data?.user ||
            null
        );

        setStatistics(
            response.data
                ?.statistics ||
            null
        );
    } catch (error) {
        console.error(
            'Ошибка загрузки Dashboard:',
            error
        );

        if (
            error.response?.status ===
            401
        ) {
            localStorage.removeItem(
                'token'
            );

            localStorage.removeItem(
                'user'
            );

            navigate('/login');
            return;
        }

        setStatistics(null);
    } finally {
        if (initial) {
            setLoading(false);
        } else {
            setStatisticsLoading(false);
        }
    }
};

    useEffect(() => {
        fetchDashboard(
            [
                dayjs().startOf('day'),
                dayjs().endOf('day')
            ],
            true
        );

        const token = localStorage.getItem('token');
        if (token && canSeeCalendar) {
            axios
                .get('/api/calendar/upcoming', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
                .then(response => {
                    setCalendarEvents(
                        Array.isArray(response.data?.items)
                            ? response.data.items
                            : []
                    );
                })
                .catch(() => {
                    setCalendarEvents([]);
                });
        }

        if (token && (isAdmin || isManager)) {
            axios
                .get('/api/bp-closing/summary', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
                .then(response => {
                    setBpClosingSummary(
                        response.data?.data || response.data || null
                    );
                })
                .catch(() => {
                    setBpClosingSummary(null);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchEstimateRanking = async (
        period = estimateRankingPeriod
    ) => {
        if (!canSeeEstimateRanking) {
            return;
        }

        const token = localStorage.getItem('token');

        if (!token || !period?.[0] || !period?.[1]) {
            return;
        }

        setEstimateRankingLoading(true);

        try {
            const response = await axios.get(
                '/api/dashboard/estimate-ranking',
                {
                    params: {
                        date_from: period[0].format(
                            'YYYY-MM-DD'
                        ),
                        date_to: period[1].format(
                            'YYYY-MM-DD'
                        )
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    withCredentials: true
                }
            );

            setEstimateRankingRows(
                Array.isArray(response.data?.rows)
                    ? response.data.rows
                    : []
            );
            setEstimateRankingByCustomer(
                Array.isArray(
                    response.data?.by_customer
                )
                    ? response.data.by_customer
                    : []
            );
        } catch (error) {
            console.error(
                'Ошибка загрузки рейтинга осмечивания:',
                error
            );
            setEstimateRankingRows([]);
            setEstimateRankingByCustomer([]);
        } finally {
            setEstimateRankingLoading(false);
        }
    };

    useEffect(() => {
        if (!canSeeEstimateRanking || loading) {
            return;
        }

        fetchEstimateRanking(estimateRankingPeriod);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSeeEstimateRanking, loading]);

    const handleStatisticsPeriodChange =
        value => {
            if (
                !value?.[0] ||
                !value?.[1]
            ) {
                return;
            }

            const nextPeriod = [
                value[0].startOf('day'),
                value[1].endOf('day')
            ];

            setStatisticsPeriod(
                nextPeriod
            );

            fetchDashboard(
                nextPeriod,
                false,
                exitPeriod
            );
        };

    const handleExitPeriodChange =
    value => {
        const nextExitPeriod =
            value?.[0] &&
            value?.[1]
                ? [
                    value[0]
                        .startOf('day'),

                    value[1]
                        .endOf('day')
                ]
                : null;

        setExitPeriod(
            nextExitPeriod
        );

        fetchDashboard(
            statisticsPeriod,
            false,
            nextExitPeriod
        );
    };

    const statisticsPeriodPresets = [
        {
            label: 'Сегодня',

            value: [
                dayjs().startOf('day'),
                dayjs().endOf('day')
            ]
        },
        {
            label: 'Вчера',

            value: [
                dayjs()
                    .subtract(1, 'day')
                    .startOf('day'),

                dayjs()
                    .subtract(1, 'day')
                    .endOf('day')
            ]
        },
        {
            label: 'Последние 7 дней',

            value: [
                dayjs()
                    .subtract(6, 'day')
                    .startOf('day'),

                dayjs().endOf('day')
            ]
        },
        {
            label: 'Текущий месяц',

            value: [
                dayjs().startOf('month'),
                dayjs().endOf('day')
            ]
        },
        {
            label: 'Прошлый месяц',

            value: [
                dayjs()
                    .subtract(1, 'month')
                    .startOf('month'),

                dayjs()
                    .subtract(1, 'month')
                    .endOf('month')
            ]
        }
    ];

    const handleLogout = () => {
        localStorage.removeItem(
            'token'
        );

        navigate('/login');
    };

    const menuItems = [
        {
            title:
                'Задачи',

            path:
                '/tasks',

            icon:
                <ProjectOutlined />
        },
        {
            title:
                'MAX',

            path:
                '/max-messenger',

            icon:
                <MessageOutlined />
        },
        {
            title:
                'Задачи Bitrix',

            path:
                '/bitrix-tasks',

            icon:
                <CheckCircleOutlined />
        },
        {
            title:
                'Календарь',

            path:
                '/calendar',

            icon:
                <CalendarOutlined />
        },
        {
            title:
                'Менеджер задач',

            path:
                '/task-manager',

            icon:
                <AppstoreOutlined />
        },
        {
            title:
                'Настройки менеджера задач',

            path:
                '/task-manager/settings',

            icon:
                <SettingOutlined />
        },
        {
            title:
                'Статьи',

            path:
                '/financial-articles',

            icon:
                <FileTextOutlined />
        },
        {
            title:
                'Номенклатура',

            path:
                '/nomenclature',

            icon:
                <OrderedListOutlined />
        },
        {
            title:
                'ТМЦ',

            path:
                '/tmc',

            icon:
                <InboxOutlined />
        },
        {
            title:
                'Проверка заказа покупателя',

            path:
                '/customer-order-compare',

            icon:
                <FileSearchOutlined />,

            managerAdminOnly:
                true
        },
        {
            title:
                'Контроль закрытия работ',

            path:
                '/bp-closing',

            icon:
                <CalendarOutlined />,

            managerAdminOnly:
                true
        },
        {
            title:
                'ВДР',

            path:
                '/invoices',

            icon:
                <DollarCircleOutlined />
        },
        {
            title:
                'Документы',

            path:
                '/registries',

            icon:
                <SolutionOutlined />
        },
        {
            title:
                'Документы 2.0',

            path:
                '/docs',

            icon:
                <SolutionOutlined />
        },
        {
            title:
                'Субподряды',

            path:
                '/subcontracts',

            icon:
                <PartitionOutlined />
        },
        {
            title:
                'Согласование документов',

            path:
                '/doc-workflows',

            icon:
                <CheckCircleOutlined />
        },
        {
            title:
                'Согласование счетов',

            path:
                '/invoice-approval',

            icon:
                <DollarCircleOutlined />
        },
        {
            title:
                'Чат сотрудников',

            path:
                '/chat',

            icon:
                <MessageOutlined />
        },
        {
            title:
                'Транскрибация',

            path:
                '/transcription',

            icon:
                <AudioOutlined />
        },
        {
            title:
                'Расчёт маршрута',
    path: '/route-calculator',
    icon: <CarOutlined />,

},
        {
            title:
                'Ежедневные отчеты',

            path:
                '/daily-reports',

            icon:
                <CalendarOutlined />
        },
        {
            title:
                'Контрагенты',

            path:
                '/counterparties',

            icon:
                <TeamOutlined />
        },
        {
            title:
                'Шаблоны',

            path:
                '/templates-incomerce',

            icon:
                <LayoutOutlined />
        },
        {
            title:
                'Сценарии',

            path:
                '/constructor',

            icon:
                <BuildOutlined />
        },
        {
            title:
                'Типы цен',

            path:
                '/price-types',

            icon:
                <TagsOutlined />
        },
        {
            title:
                'Переменные',

            path:
                '/edit-stat',

            icon:
                <EditOutlined />
        },
        {
            title:
                'Пользователи',

            path:
                '/users',

            icon:
                <UserOutlined />,

            usersManageOnly:
                true
        },
        {
            title:
                'Отделы и должности',

            path:
                '/org-structure',

            icon:
                <ApartmentOutlined />,

            usersManageOnly:
                true
        },
        {
            title:
                'Журнал доступа',

            path:
                '/user-activity',

            icon:
                <HistoryOutlined />,

            usersManageOnly:
                true
        },
        {
    title:
        'HR-аналитика',

    path:
        '/bitrix-analytics',

    icon:
        <TeamOutlined />,

    hrOnly:
        true
},
        {
            title:
                'Сделки HR',

            path:
                '/hr/deals',

            icon:
                <SolutionOutlined />,

            hrOnly:
                true
        },
        {
            title:
                'Учёт персонала',

            path:
                '/hr/staff-registry',

            icon:
                <ContactsOutlined />,

            hrOnly:
                true
        },
        {
            title:
                'Права доступа',

            path:
                '/priveleg',

            icon:
                <LockOutlined />,

            adminOnly:
                true
        },
        {
            title:
                'Мониторинг слов',

            path:
                '/keyword-watch',

            icon:
                <SearchOutlined />,

            adminOnly:
                true
        },
        {
            title:
                'Госключ',

            path:
                '/goskey',

            icon:
                <SafetyCertificateOutlined />,

            adminOnly:
                true
        }
    ];

    const isHrAccess =
        isAdmin || currentRoleId === 6 || currentRoleId === 8;

    const canManageUsers =
        isAdmin || currentRoleId === 7;

    const visibleMenuItems =
        menuItems.filter(
            item => {
                if (item.path === '/max-messenger' && !isAdmin) {
                    if (
                        !canAtLeast(
                            getCachedPageAccess()?.['max-messenger'],
                            'view'
                        )
                    ) {
                        return false;
                    }
                }

                if (item.adminOnly && !isAdmin) {
                    return false;
                }

                if (
                    item.managerAdminOnly &&
                    currentRoleId !== 1 &&
                    currentRoleId !== 2
                ) {
                    return false;
                }

                if (
                    item.usersManageOnly &&
                    !canManageUsers
                ) {
                    return false;
                }

                if (item.hrOnly && !isHrAccess) {
                    return false;
                }

                /*
                 * Для ролей HR / Руководитель ОП показываем только HR-пункты.
                 * Транскрибация добавляется лично, если на неё выдан доступ.
                 */
                if (
                    (currentRoleId === 6 || currentRoleId === 8) &&
                    !item.hrOnly
                ) {
                    const grantedKey = pathToPageKey(item.path);
                    const granted = getCachedPageAccess();
                    if (
                        accessTick < 0 ||
                        grantedKey !== 'transcription' ||
                        !granted ||
                        !canAtLeast(granted[grantedKey], 'view')
                    ) {
                        return false;
                    }
                }

                if (
                    currentRoleId === 7 &&
                    !item.usersManageOnly
                ) {
                    return false;
                }

                const key = pathToPageKey(item.path);
                const access = getCachedPageAccess();
                if (
                    key &&
                    access &&
                    !isAdmin &&
                    !canAtLeast(access[key], 'view')
                ) {
                    return false;
                }

                return true;
            }
        );

    

    const summary =
        statistics?.summary || {};

    const estimate =
        statistics?.estimate || {};

    const finance =
        statistics?.finance || {};

    const orders =
        statistics?.orders || {};

    const employees =
        Array.isArray(
            statistics?.employees
        )
            ? statistics.employees
            : [];

    const closurePercent =
        Number(
            summary.total_tasks
        ) > 0
            ? Math.round(
                (
                    Number(
                        summary.closed_tasks
                    ) /
                    Number(
                        summary.total_tasks
                    )
                ) *
                100
            )
            : 0;

    const estimateChartData =
        useMemo(
            () => [
                {
                    name:
                        'Без ВДР',

                    value:
                        Number(
                            estimate.without_estimate
                        ) || 0
                },
                {
                    name:
                        'Частично осмечено',

                    value:
                        Number(
                            estimate.partial_estimate
                        ) || 0
                },
                {
                    name:
                        'Полностью осмечено',

                    value:
                        Number(
                            estimate.full_estimate
                        ) || 0
                }
            ],
            [estimate]
        );

    const documentChartData =
    useMemo(
        () => [
            {
                name:
                    'Заказы покупателя',

                value:
                    Number(
                        orders.customer_orders_count
                    ) || 0
            },
            {
                name:
                    'Заказы поставщика',

                value:
                    Number(
                        orders.supplier_orders_count
                    ) || 0
            },
            {
                name:
                    'Акты покупателя',

                value:
                    Number(
                        orders.customer_acts_count
                    ) || 0
            },
            {
                name:
                    'Акты поставщика',

                value:
                    Number(
                        orders.supplier_acts_count
                    ) || 0
            }
        ],
        [orders]
    );

const taskStages =
    useMemo(
        () =>
            Array.isArray(
                statistics?.task_stages
            )
                ? statistics.task_stages
                    .map(stage =>
                        String(
                            stage ||
                            'Этап не указан'
                        ).trim()
                    )
                    .filter(Boolean)
                : [],
        [
            statistics?.task_stages
        ]
    );

const taskStageDynamics =
    useMemo(
        () => {
            const source =
                Array.isArray(
                    statistics
                        ?.task_stage_dynamics
                )
                    ? statistics
                        .task_stage_dynamics
                    : [];

            return source.map(item => {
                const normalized = {
                    ...item,

                    date:
                        item.date
                            ? dayjs(
                                item.date
                            ).format(
                                'DD.MM'
                            )
                            : '',

                    total:
                        Number(
                            item.total
                        ) || 0
                };

                taskStages.forEach(stage => {
                    normalized[stage] =
                        Number(
                            item[stage]
                        ) || 0;
                });

                return normalized;
            });
        },
        [
            statistics
                ?.task_stage_dynamics,

            taskStages
        ]
    );

    const executorStageStatistics =
    Array.isArray(
        statistics
            ?.executor_stage_statistics
    )
        ? statistics
            .executor_stage_statistics
        : [];

    const formatMoney =
        value =>
            Number(
                value || 0
            ).toLocaleString(
                'ru-RU',
                {
                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2
                }
            );

    const normalizePercent =
        value =>
            Math.min(
                100,
                Math.max(
                    0,
                    Number(value) || 0
                )
            );

    const getProfitColor =
        value => {
            const amount =
                Number(value) || 0;

            if (amount < 0) {
                return 'red';
            }

            if (amount === 0) {
                return 'default';
            }

            return 'green';
        };

    const getProfitabilityColor =
        value => {
            const percent =
                Number(value) || 0;

            if (percent < 0) {
                return 'red';
            }

            if (percent < 20) {
                return 'orange';
            }

            return 'green';
        };

    const customerStatistics =
        Array.isArray(
            statistics?.customers
        )
            ? statistics.customers
            : [];

    const customerColumns = [
    {
        title:
            'Заказчик',

        dataIndex:
            'customer_name',

        key:
            'customer_name',

        fixed:
            'left',

        width:
            270,

        sorter:
            (
                first,
                second
            ) =>
                String(
                    first.customer_name ||
                    ''
                ).localeCompare(
                    String(
                        second.customer_name ||
                        ''
                    ),
                    'ru'
                ),

        render:
            value => (
                <Space>
                    <Avatar
                        size="small"
                        icon={
                            <TeamOutlined />
                        }
                    />

                    <Text strong>
                        {value ||
                            'Заказчик не указан'}
                    </Text>
                </Space>
            )
    },

    {
        title:
            'Заявки',

        children: [
            {
                title:
                    'Всего',

                dataIndex:
                    'tasks_count',

                key:
                    'tasks_count',

                width:
                    85,

                align:
                    'right',

                sorter:
                    (
                        first,
                        second
                    ) =>
                        Number(
                            first.tasks_count
                        ) -
                        Number(
                            second.tasks_count
                        ),

                render:
                    value => (
                        <Text strong>
                            {Number(value) ||
                                0}
                        </Text>
                    )
            },
            {
    title:
        'Доходных строк',

    dataIndex:
        'income_rows',

    key:
        'income_rows',

    width:
        125,

    align:
        'right',

    sorter:
        (
            first,
            second
        ) =>
            Number(
                first.income_rows
            ) -
            Number(
                second.income_rows
            ),

    render:
        value =>
            Number(value) || 0
},
{
    title:
        'Доход',

    dataIndex:
        'income_amount',

    key:
        'income_amount',

    width:
        165,

    align:
        'right',

    sorter:
        (
            first,
            second
        ) =>
            Number(
                first.income_amount
            ) -
            Number(
                second.income_amount
            ),

    render:
        value => (
            <Text strong>
                {formatMoney(
                    value
                )}{' '}
                ₽
            </Text>
        )
},

            {
                title:
                    'В работе',

                dataIndex:
                    'tasks_in_work',

                key:
                    'tasks_in_work',

                width:
                    95,

                align:
                    'right'
            },

            {
                title:
                    'Осмечено',

                dataIndex:
                    'estimated_tasks',

                key:
                    'estimated_tasks',

                width:
                    95,

                align:
                    'right'
            },

            {
                title:
                    'Осмечивание',

                dataIndex:
                    'estimated_percent',

                key:
                    'estimated_percent',

                width:
                    155,

                render:
                    value => (
                        <Progress
                            percent={
                                normalizePercent(
                                    value
                                )
                            }

                            size="small"

                            status={
                                Number(value) >=
                                100
                                    ? 'success'
                                    : 'normal'
                            }
                        />
                    )
            }
        ]
    },

    {
        title:
            'Заказы покупателя',

        children: [
            {
                title:
                    'Заявок вошло',

                dataIndex:
                    'tasks_in_customer_orders',

                key:
                    'tasks_in_customer_orders',

                width:
                    110,

                align:
                    'right'
            },

            {
                title:
                    'Заказов',

                dataIndex:
                    'customer_orders_count',

                key:
                    'customer_orders_count',

                width:
                    90,

                align:
                    'right'
            },

            {
                title:
                    'Доходная сумма',

                dataIndex:
                    'customer_orders_amount',

                key:
                    'customer_orders_amount',

                width:
                    165,

                align:
                    'right',

                render:
                    value =>
                        `${formatMoney(
                            value
                        )} ₽`
            },

            {
                title:
                    'Закрыто заявок',

                dataIndex:
                    'tasks_with_closed_customer_order',

                key:
                    'tasks_with_closed_customer_order',

                width:
                    125,

                align:
                    'right'
            },

            {
                title:
                    'Закрыто актами',

                dataIndex:
                    'customer_act_closed_amount',

                key:
                    'customer_act_closed_amount',

                width:
                    165,

                align:
                    'right',

                render:
                    value =>
                        `${formatMoney(
                            value
                        )} ₽`
            }
        ]
    },

    {
        title:
            'Расходы по заявкам',

        children: [
            {
                title:
                    'Строк',

                dataIndex:
                    'expense_rows',

                key:
                    'expense_rows',

                width:
                    85,

                align:
                    'right'
            },

            {
                title:
                    'Общий расход',

                dataIndex:
                    'expense_amount',

                key:
                    'expense_amount',

                width:
                    165,

                align:
                    'right',

                sorter:
                    (
                        first,
                        second
                    ) =>
                        Number(
                            first.expense_amount
                        ) -
                        Number(
                            second.expense_amount
                        ),

                render:
                    value => (
                        <Text strong>
                            {formatMoney(
                                value
                            )}{' '}
                            ₽
                        </Text>
                    )
            }
        ]
    },

    {
        title:
            'Заказы поставщика',

        children: [
            {
                title:
                    'Заказов',

                dataIndex:
                    'supplier_orders_count',

                key:
                    'supplier_orders_count',

                width:
                    90,

                align:
                    'right'
            },

            {
                title:
                    'Передано строк',

                dataIndex:
                    'distributed_expense_rows',

                key:
                    'distributed_expense_rows',

                width:
                    115,

                align:
                    'right'
            },

            {
                title:
                    'Передано на сумму',

                dataIndex:
                    'distributed_expense_amount',

                key:
                    'distributed_expense_amount',

                width:
                    170,

                align:
                    'right',

                render:
                    value =>
                        `${formatMoney(
                            value
                        )} ₽`
            },

            {
                title:
                    'Не распределено',

                dataIndex:
                    'remaining_expense_amount',

                key:
                    'remaining_expense_amount',

                width:
                    160,

                align:
                    'right',

                render:
                    value => (
                        <Tag
                            color={
                                Number(value) > 0
                                    ? 'orange'
                                    : 'green'
                            }
                        >
                            {formatMoney(
                                value
                            )}{' '}
                            ₽
                        </Tag>
                    )
            }
        ]
    },

    {
        title:
            'Акты поставщика',

        children: [
            {
                title:
                    'Актов',

                dataIndex:
                    'supplier_acts_count',

                key:
                    'supplier_acts_count',

                width:
                    85,

                align:
                    'right'
            },

            {
                title:
                    'Закрыто строк',

                dataIndex:
                    'supplier_act_closed_rows',

                key:
                    'supplier_act_closed_rows',

                width:
                    110,

                align:
                    'right'
            },

            {
                title:
                    'Закрыто на сумму',

                dataIndex:
                    'supplier_act_closed_amount',

                key:
                    'supplier_act_closed_amount',

                width:
                    170,

                align:
                    'right',

                render:
                    value =>
                        `${formatMoney(
                            value
                        )} ₽`
            }
        ]
    }
];

    const employeeColumns = [
        {
            title:
                'Сотрудник',

            dataIndex:
                'user_name',

            key:
                'user_name',

            fixed:
                'left',

            width:
                220,

            sorter:
                (
                    first,
                    second
                ) =>
                    String(
                        first.user_name ||
                        ''
                    ).localeCompare(
                        String(
                            second.user_name ||
                            ''
                        ),
                        'ru'
                    ),

            render:
                value => (
                    <Space>
                        <Avatar
                            size="small"
                            icon={
                                <UserOutlined />
                            }
                        />

                        <Text strong>
                            {value ||
                                'Не указан'}
                        </Text>
                    </Space>
                )
        },
        {
            title:
                'Закреплено задач',

            dataIndex:
                'assigned_tasks',

            key:
                'assigned_tasks',

            width:
                145,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.assigned_tasks
                    ) -
                    Number(
                        second.assigned_tasks
                    ),

            render:
                value =>
                    Number(value) || 0
        },
        {
            title:
                'Заполнено ВДР',

            dataIndex:
                'tasks_with_vdr',

            key:
                'tasks_with_vdr',

            width:
                140,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.tasks_with_vdr
                    ) -
                    Number(
                        second.tasks_with_vdr
                    ),

            render:
                value => (
                    <Text strong>
                        {Number(value) ||
                            0}
                    </Text>
                )
        },
        {
            title:
                'Без ВДР',

            dataIndex:
                'tasks_without_vdr',

            key:
                'tasks_without_vdr',

            width:
                100,

            align:
                'center',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.tasks_without_vdr
                    ) -
                    Number(
                        second.tasks_without_vdr
                    ),

            render:
                value => {
                    const amount =
                        Number(value) ||
                        0;

                    return (
                        <Tag
                            color={
                                amount > 0
                                    ? 'orange'
                                    : 'green'
                            }
                        >
                            {amount}
                        </Tag>
                    );
                }
        },
        {
            title:
                'Заполнение',

            dataIndex:
                'completion_percent',

            key:
                'completion_percent',

            width:
                180,

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.completion_percent
                    ) -
                    Number(
                        second.completion_percent
                    ),

            render:
                value => {
                    const percent =
                        Number(value) ||
                        0;

                    return (
                        <Progress
                            percent={
                                percent
                            }

                            size="small"

                            status={
                                percent >= 100
                                    ? 'success'
                                    : 'normal'
                            }
                        />
                    );
                }
        },
        {
            title:
                'Доходных строк',

            dataIndex:
                'income_rows',

            key:
                'income_rows',

            width:
                140,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.income_rows
                    ) -
                    Number(
                        second.income_rows
                    ),

            render:
                value =>
                    Number(value) || 0
        },
        {
            title:
                'Расходных строк',

            dataIndex:
                'expense_rows',

            key:
                'expense_rows',

            width:
                145,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.expense_rows
                    ) -
                    Number(
                        second.expense_rows
                    ),

            render:
                value =>
                    Number(value) || 0
        },
        {
            title:
                'Всего строк',

            dataIndex:
                'total_rows',

            key:
                'total_rows',

            width:
                115,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.total_rows
                    ) -
                    Number(
                        second.total_rows
                    ),

            defaultSortOrder:
                'descend',

            render:
                value => (
                    <Text strong>
                        {Number(value) ||
                            0}
                    </Text>
                )
        },
        {
            title:
                'Доходная сумма',

            dataIndex:
                'income_amount',

            key:
                'income_amount',

            width:
                170,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.income_amount
                    ) -
                    Number(
                        second.income_amount
                    ),

            render:
                value =>
                    `${formatMoney(
                        value
                    )} ₽`
        },
        {
            title:
                'Расходная сумма',

            dataIndex:
                'expense_amount',

            key:
                'expense_amount',

            width:
                170,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.expense_amount
                    ) -
                    Number(
                        second.expense_amount
                    ),

            render:
                value =>
                    `${formatMoney(
                        value
                    )} ₽`
        },
        {
            title:
                'Расчётная прибыль',

            dataIndex:
                'profit',

            key:
                'profit',

            width:
                175,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.profit
                    ) -
                    Number(
                        second.profit
                    ),

            render:
                value => {
                    const amount =
                        Number(value) ||
                        0;

                    return (
                        <Tag
                            color={
                                getProfitColor(
                                    amount
                                )
                            }
                            style={{
                                margin: 0
                            }}
                        >
                            {formatMoney(
                                amount
                            )}{' '}
                            ₽
                        </Tag>
                    );
                }
        },
        {
    title:
        'Первичных заполнений',

    dataIndex:
        'initial_fill_actions',

    key:
        'initial_fill_actions',

    width:
        170,

    align:
        'right',

    sorter:
        (
            first,
            second
        ) =>
            Number(
                first
                    .initial_fill_actions
            ) -
            Number(
                second
                    .initial_fill_actions
            ),

    render:
        value =>
            Number(value) || 0
},
        {
            title:
                'Добавлено строк',

            dataIndex:
                'added_rows_actions',

            key:
                'added_rows_actions',

            width:
                145,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.added_rows_actions
                    ) -
                    Number(
                        second.added_rows_actions
                    ),

            render:
                value =>
                    Number(value) || 0
        },
        {
            title:
                'Изменено строк',

            dataIndex:
                'updated_rows_actions',

            key:
                'updated_rows_actions',

            width:
                145,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.updated_rows_actions
                    ) -
                    Number(
                        second.updated_rows_actions
                    ),

            render:
                value =>
                    Number(value) || 0
        },
        {
    title:
        'Удалено строк',

    dataIndex:
        'deleted_rows_actions',

    key:
        'deleted_rows_actions',

    width:
        145,

    align:
        'right',

    sorter:
        (
            first,
            second
        ) =>
            Number(
                first
                    .deleted_rows_actions
            ) -
            Number(
                second
                    .deleted_rows_actions
            ),

    render:
        value => (
            <Tag
                color={
                    Number(value) > 0
                        ? 'red'
                        : 'default'
                }
            >
                {Number(value) || 0}
            </Tag>
        )
},
{
    title:
        'Смен статуса ВДР',

    dataIndex:
        'status_actions',

    key:
        'status_actions',

    width:
        155,

    align:
        'right',

    sorter:
        (
            first,
            second
        ) =>
            Number(
                first.status_actions
            ) -
            Number(
                second.status_actions
            ),

    render:
        value =>
            Number(value) || 0
},
        {
            title:
                'Всего действий',

            dataIndex:
                'actions_count',

            key:
                'actions_count',

            width:
                130,

            align:
                'right',

            sorter:
                (
                    first,
                    second
                ) =>
                    Number(
                        first.actions_count
                    ) -
                    Number(
                        second.actions_count
                    ),

            render:
                value =>
                    Number(value) || 0
        },
        {
            title:
                'Последняя активность',

            dataIndex:
                'last_activity_at',

            key:
                'last_activity_at',

            width:
                180,

            sorter:
                (
                    first,
                    second
                ) =>
                    dayjs(
                        first.last_activity_at
                    ).valueOf() -
                    dayjs(
                        second.last_activity_at
                    ).valueOf(),

            render:
                value =>
                    value
                        ? dayjs(
                            value
                        ).format(
                            'DD.MM.YYYY HH:mm'
                        )
                        : '—'
        }
    ];
    const executorStageColumns =
    useMemo(
        () => [
            {
                title:
                    'Исполнитель',

                dataIndex:
                    'executor_name',

                key:
                    'executor_name',

                fixed:
                    'left',

                width:
                    260,

                sorter:
                    (
                        first,
                        second
                    ) =>
                        String(
                            first.executor_name ||
                            ''
                        ).localeCompare(
                            String(
                                second.executor_name ||
                                ''
                            ),
                            'ru'
                        ),

                render:
                    value => (
                        <Space>
                            <Avatar
                                size="small"
                                icon={
                                    <UserOutlined />
                                }
                            />

                            <Text strong>
                                {value ||
                                    'Исполнитель не назначен'}
                            </Text>
                        </Space>
                    )
            },

            {
                title:
                    'Всего заявок',

                dataIndex:
                    'total_tasks',

                key:
                    'total_tasks',

                fixed:
                    'left',

                width:
                    125,

                align:
                    'right',

                defaultSortOrder:
                    'descend',

                sorter:
                    (
                        first,
                        second
                    ) =>
                        Number(
                            first.total_tasks
                        ) -
                        Number(
                            second.total_tasks
                        ),

                render:
                    value => (
                        <Text strong>
                            {Number(value) ||
                                0}
                        </Text>
                    )
            },

            ...taskStages.map(
                (
                    stage,
                    index
                ) => ({
                    title:
                        stage,

                    dataIndex:
                        stage,

                    key:
                        `executor-stage-${index}`,

                    width:
                        155,

                    align:
                        'right',

                    sorter:
                        (
                            first,
                            second
                        ) =>
                            Number(
                                first[stage]
                            ) -
                            Number(
                                second[stage]
                            ),

                    render:
                        value => {
                            const tasksCount =
                                Number(value) ||
                                0;

                            return (
                                <Tag
                                    color={
                                        tasksCount > 0
                                            ? 'blue'
                                            : 'default'
                                    }
                                >
                                    {tasksCount}
                                </Tag>
                            );
                        }
                })
            )
        ],
        [
            taskStages
        ]
    );

    const displayUser = user || currentUser;

    if (!displayUser && loading) {
        return (
            <div
                style={{
                    display:
                        'flex',

                    justifyContent:
                        'center',

                    alignItems:
                        'center',

                    height:
                        '100vh'
                }}
            >
                <Spin
                    size="large"
                    tip="Загрузка данных..."
                />
            </div>
        );
    }

    if (!displayUser) {
        return null;
    }

    return (
        <Layout
            style={{
                minHeight:
                    '100vh',

                background:
                    '#f0f2f5',

                padding:
                    24
            }}
        >
            <Content
                style={{
                    maxWidth:
                        1500,

                    margin:
                        '0 auto',

                    width:
                        '100%'
                }}
            >
                {/* Профиль */}

                <Card
                    bordered={
                        false
                    }

                    style={{
                        marginBottom:
                            24,

                        borderRadius:
                            12,

                        boxShadow:
                            '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                >
                    <Row
                        align="middle"

                        justify="space-between"

                        gutter={[
                            16,
                            16
                        ]}
                    >
                        <Col>
                            <Space
                                size="middle"
                            >
                                <Avatar
                                    size={64}

                                    src={
                                        displayUser.avatar_url ||
                                        undefined
                                    }

                                    icon={
                                        <UserOutlined />
                                    }

                                    style={{
                                        backgroundColor:
                                            '#1890ff'
                                    }}
                                />

                                <div>
                                    <Title
                                        level={2}

                                        style={{
                                            margin: 0
                                        }}
                                    >
                                        Панель управления
                                    </Title>

                                    <Text
                                        type="secondary"
                                    >
                                        Добро пожаловать,{' '}

                                        <Text strong>
                                            {displayUser.login}
                                        </Text>

                                        {displayUser.email
                                            ? ` (${displayUser.email})`
                                            : ''}
                                    </Text>

                                    {isAdmin && (
                                        <div
                                            style={{
                                                marginTop:
                                                    6
                                            }}
                                        >
                                            <Tag
                                                color="red"
                                            >
                                                Администратор
                                            </Tag>
                                        </div>
                                    )}
                                </div>
                            </Space>
                        </Col>

                        <Col>
                            <Button
                                type="primary"

                                danger

                                ghost

                                icon={
                                    <LogoutOutlined />
                                }

                                onClick={
                                    handleLogout
                                }
                            >
                                Выйти
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Навигация */}

                <Divider
                    orientation="left"
                >
                    Навигация по системе
                </Divider>

                <Row
                    gutter={[
                        16,
                        16
                    ]}
                >
                    {visibleMenuItems.map(
                        item => (
                            <Col
                                xs={12}
                                sm={8}
                                md={6}
                                lg={4}

                                key={
                                    item.path
                                }
                            >
                                <Link
                                    to={
                                        item.path
                                    }
                                >
                                    <Card
                                        hoverable

                                        style={{
                                            textAlign:
                                                'center',

                                            borderRadius:
                                                8,

                                            height:
                                                '100%',

                                            display:
                                                'flex',

                                            flexDirection:
                                                'column',

                                            justifyContent:
                                                'center'
                                        }}

                                        styles={{
                                            body: {
                                                padding:
                                                    '20px 10px'
                                            }
                                        }}
                                    >
                                        <Space
                                            direction="vertical"

                                            size="small"
                                        >
                                            <div
                                                style={{
                                                    fontSize:
                                                        24,

                                                    color:
                                                        '#1890ff'
                                                }}
                                            >
                                                {item.icon}
                                            </div>

                                            <Text strong>
                                                {item.title}
                                            </Text>
                                        </Space>
                                    </Card>
                                </Link>
                            </Col>
                        )
                    )}
                </Row>

                {(isAdmin || isManager) && bpClosingSummary ? (
                    <Card
                        title="Контроль закрытия работ"
                        extra={
                            <Link to="/bp-closing">Открыть реестр</Link>
                        }
                        style={{
                            marginTop: 24,
                            borderRadius: 10
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={8} md={6}>
                                <Statistic
                                    title="В работе ₽"
                                    value={
                                        bpClosingSummary.amount_in_work || 0
                                    }
                                    precision={0}
                                    groupSeparator=" "
                                    valueStyle={{ color: '#1677ff' }}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Statistic
                                    title="Ожидает оплату ₽"
                                    value={
                                        bpClosingSummary.amount_awaiting_payment ||
                                        0
                                    }
                                    precision={0}
                                    groupSeparator=" "
                                    valueStyle={{ color: '#08979c' }}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <Statistic
                                    title="Просрочено"
                                    value={bpClosingSummary.overdue || 0}
                                    valueStyle={{
                                        color:
                                            (bpClosingSummary.overdue || 0) > 0
                                                ? '#cf1322'
                                                : undefined
                                    }}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <Statistic
                                    title="Срок скоро"
                                    value={bpClosingSummary.due_soon || 0}
                                    valueStyle={{ color: '#d48806' }}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <Statistic
                                    title="Активных"
                                    value={bpClosingSummary.total || 0}
                                />
                            </Col>
                        </Row>
                        {(bpClosingSummary.problems || []).length > 0 ? (
                            <div style={{ marginTop: 16 }}>
                                <Text type="secondary">
                                    Проблемные записи
                                </Text>
                                {(bpClosingSummary.problems || [])
                                    .slice(0, 5)
                                    .map(item => (
                                        <div
                                            key={item.id}
                                            style={{
                                                display: 'flex',
                                                gap: 12,
                                                padding: '8px 0',
                                                borderBottom:
                                                    '1px solid #f0f0f0',
                                                alignItems: 'baseline'
                                            }}
                                        >
                                            <Tag color="red">просрочено</Tag>
                                            <Text strong style={{ flex: 1 }}>
                                                {item.region} · {item.period}
                                            </Text>
                                            <Text type="secondary">
                                                {item.current_step?.title ||
                                                    '—'}
                                            </Text>
                                            <Link
                                                to={`/bp-closing?overdue=1`}
                                            >
                                                Открыть
                                            </Link>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div style={{ marginTop: 12 }}>
                                <Link to="/bp-closing?overdue=1">
                                    <Button>
                                        Показать просроченные
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </Card>
                ) : null}

                {canSeeCalendar && calendarEvents.length > 0 ? (
                    <Card
                        title="Ближайшие встречи и события"
                        extra={
                            <Link to="/calendar">Открыть календарь</Link>
                        }
                        style={{
                            marginTop: 24,
                            borderRadius: 10
                        }}
                    >
                        {calendarEvents.map(item => (
                            <div
                                key={item.id}
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'baseline',
                                    padding: '8px 0',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                            >
                                <Tag
                                    color={
                                        item.type === 'meeting'
                                            ? 'blue'
                                            : 'green'
                                    }
                                >
                                    {item.type_name || item.type}
                                </Tag>
                                <Text strong style={{ flex: 1 }}>
                                    {item.title}
                                </Text>
                                <Text type="secondary">
                                    {item.all_day
                                        ? dayjs(item.starts_at).format(
                                              'DD.MM.YYYY'
                                          )
                                        : dayjs(item.starts_at).format(
                                              'DD.MM.YYYY HH:mm'
                                          )}
                                </Text>
                            </div>
                        ))}
                    </Card>
                ) : canSeeCalendar ? (
                    <Card
                        style={{
                            marginTop: 24,
                            borderRadius: 10
                        }}
                    >
                        <Space
                            style={{
                                width: '100%',
                                justifyContent: 'space-between'
                            }}
                            wrap
                        >
                            <Space>
                                <CalendarOutlined />
                                <Text>
                                    Нет ближайших встреч — добавьте событие в календарь
                                </Text>
                            </Space>
                            <Link to="/calendar">
                                <Button type="primary">
                                    Открыть календарь
                                </Button>
                            </Link>
                        </Space>
                    </Card>
                ) : null}

                {/* Административная статистика */}

                {isAdmin && (
    <>
        <Card
            size="small"
            style={{
                marginBottom: 16,
                borderRadius: 10,
                marginTop: 50
            }}
        >
            <Row
                gutter={[16, 16]}
                align="middle"
                justify="space-between"
            
            >
                <Col>
                    <Title
                        level={4}
                        style={{
                            margin: 0
                        }}
                    >
                        Сводка администратора
                    </Title>

                    <Text type="secondary">
                        Статистика за период:{' '}

                        <Text strong>
                            {statisticsPeriod[0]
                                .format(
                                    'DD.MM.YYYY'
                                )}
                        </Text>

                        {' — '}

                        <Text strong>
                            {statisticsPeriod[1]
                                .format(
                                    'DD.MM.YYYY'
                                )}
                        </Text>
                    </Text>
                </Col>

                <Col>
    <Space
        wrap
        align="end"
    >
        <div>
            <Text
                type="secondary"
                style={{
                    display:
                        'block',

                    marginBottom:
                        4
                }}
            >
                Дата создания заявки
            </Text>

            <RangePicker
                allowClear={
                    false
                }

                value={
                    statisticsPeriod
                }

                format="DD.MM.YYYY"

                presets={
                    statisticsPeriodPresets
                }

                onChange={
                    handleStatisticsPeriodChange
                }
            />
        </div>

        <div>
            <Text
                type="secondary"
                style={{
                    display:
                        'block',

                    marginBottom:
                        4
                }}
            >
                Дата покидания сайта
            </Text>

            <RangePicker
                allowClear

                value={
                    exitPeriod
                }

                format="DD.MM.YYYY"

                onChange={
                    handleExitPeriodChange
                }
            />
        </div>

        <Button
            icon={
                <ReloadOutlined />
            }

            loading={
                statisticsLoading
            }

            onClick={() =>
                fetchDashboard(
                    statisticsPeriod,
                    false,
                    exitPeriod
                )
            }
        >
            Обновить
        </Button>
    </Space>
</Col>
            </Row>
        </Card>

        {loading && !statistics ? (
            <div
                style={{
                    textAlign: 'center',
                    padding: '48px 0'
                }}
            >
                <Spin
                    size="large"
                    tip="Загрузка статистики..."
                />
            </div>
        ) : !statistics ? (
            <Alert
                type="warning"
                showIcon
                message="Статистика не получена"
                description="Проверьте ответ /api/dashboard и права администратора."
            />
        ) : (
            <Spin
                spinning={
                    statisticsLoading
                }
            >

                        <Divider
    orientation="left"
>
    Документы и заказы
</Divider>

<Row
    gutter={[16, 16]}
    style={{
        marginBottom: 16
    }}
>
    <Col
        xs={24}
        sm={12}
        xl={6}
    >
        <Card
            style={{
                height: '100%'
            }}
        >
            <Statistic
                title="Заказы покупателя"

                value={
                    Number(
                        orders.customer_orders_count
                    ) || 0
                }

                prefix={
                    <SolutionOutlined />
                }
            />

            <Text type="secondary">
                Сумма:{' '}

                <Text strong>
                    {formatMoney(
                        orders.customer_orders_amount
                    )}{' '}
                    ₽
                </Text>
            </Text>
        </Card>
    </Col>

    <Col
        xs={24}
        sm={12}
        xl={6}
    >
        <Card
            style={{
                height: '100%'
            }}
        >
            <Statistic
                title="Заказы поставщика"

                value={
                    Number(
                        orders.supplier_orders_count
                    ) || 0
                }

                prefix={
                    <SolutionOutlined />
                }
            />

            <Text type="secondary">
                Сумма:{' '}

                <Text strong>
                    {formatMoney(
                        orders.supplier_orders_amount
                    )}{' '}
                    ₽
                </Text>
            </Text>
        </Card>
    </Col>

    <Col
        xs={24}
        sm={12}
        xl={6}
    >
        <Card
            style={{
                height: '100%'
            }}
        >
            <Statistic
                title="Акты покупателя"

                value={
                    Number(
                        orders.customer_acts_count
                    ) || 0
                }

                prefix={
                    <FileTextOutlined />
                }
            />

            <Text type="secondary">
                Сумма:{' '}

                <Text strong>
                    {formatMoney(
                        orders.customer_acts_amount
                    )}{' '}
                    ₽
                </Text>
            </Text>
        </Card>
    </Col>

    <Col
        xs={24}
        sm={12}
        xl={6}
    >
        <Card
            style={{
                height: '100%'
            }}
        >
            <Statistic
                title="Акты поставщика"

                value={
                    Number(
                        orders.supplier_acts_count
                    ) || 0
                }

                prefix={
                    <FileTextOutlined />
                }
            />

            <Text type="secondary">
                Сумма:{' '}

                <Text strong>
                    {formatMoney(
                        orders.supplier_acts_amount
                    )}{' '}
                    ₽
                </Text>
            </Text>
        </Card>
    </Col>
</Row>

<Row
    gutter={[16, 16]}
    style={{
        marginBottom: 16
    }}
>
    {/* Заказы покупателя → акты */}

    <Col
        xs={24}
        xl={8}
    >
        <Card
            title={
                'Закрытие заказов ' +
                'покупателя актами'
            }

            style={{
                height: '100%'
            }}
        >
            <Row
                gutter={16}
                align="middle"
            >
                <Col flex="130px">
                    <Progress
                        type="circle"

                        percent={
                            normalizePercent(
                                orders
                                    .customer_act_closed_percent
                            )
                        }

                        size={105}

                        status={
                            Number(
                                orders
                                    .customer_act_closed_percent
                            ) >= 100
                                ? 'success'
                                : 'normal'
                        }
                    />
                </Col>

                <Col flex="auto">
                    <Space
                        direction="vertical"
                        size={4}
                    >
                        <Text>
                            Всего строк:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .customer_act_total_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Закрыто актами:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .customer_act_closed_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Осталось:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .customer_act_remaining_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Закрытая сумма:{' '}

                            <Text strong>
                                {formatMoney(
                                    orders
                                        .customer_act_closed_amount
                                )}{' '}
                                ₽
                            </Text>
                        </Text>

                        <Text>
                            Полностью закрытых заказов:{' '}

                            <Tag color="green">
                                {Number(
                                    orders
                                        .customer_orders_fully_closed
                                ) || 0}
                            </Tag>
                        </Text>
                    </Space>
                </Col>
            </Row>
        </Card>
    </Col>

    {/* Заказы покупателя → поставщики */}

    <Col
        xs={24}
        xl={8}
    >
        <Card
            title={
                'Распределение расходов ' +
                'заказами поставщика'
            }

            style={{
                height: '100%'
            }}
        >
            <Row
                gutter={16}
                align="middle"
            >
                <Col flex="130px">
                    <Progress
                        type="circle"

                        percent={
                            normalizePercent(
                                orders
                                    .customer_distributed_percent
                            )
                        }

                        size={105}

                        status={
                            Number(
                                orders
                                    .customer_distributed_percent
                            ) >= 100
                                ? 'success'
                                : 'normal'
                        }
                    />
                </Col>

                <Col flex="auto">
                    <Space
                        direction="vertical"
                        size={4}
                    >
                        <Text>
                            Расходных строк:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .customer_expense_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Распределено:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .customer_distributed_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Осталось:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .customer_remaining_expense_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Распределённая сумма:{' '}

                            <Text strong>
                                {formatMoney(
                                    orders
                                        .customer_distributed_amount
                                )}{' '}
                                ₽
                            </Text>
                        </Text>

                        <Text>
                            Полностью распределено заказов:{' '}

                            <Tag color="blue">
                                {Number(
                                    orders
                                        .customer_orders_fully_distributed
                                ) || 0}
                            </Tag>
                        </Text>
                    </Space>
                </Col>
            </Row>
        </Card>
    </Col>

    {/* Заказы поставщика → акты */}

    <Col
        xs={24}
        xl={8}
    >
        <Card
            title={
                'Закрытие заказов ' +
                'поставщика актами'
            }

            style={{
                height: '100%'
            }}
        >
            <Row
                gutter={16}
                align="middle"
            >
                <Col flex="130px">
                    <Progress
                        type="circle"

                        percent={
                            normalizePercent(
                                orders
                                    .supplier_closed_percent
                            )
                        }

                        size={105}

                        status={
                            Number(
                                orders
                                    .supplier_closed_percent
                            ) >= 100
                                ? 'success'
                                : 'normal'
                        }
                    />
                </Col>

                <Col flex="auto">
                    <Space
                        direction="vertical"
                        size={4}
                    >
                        <Text>
                            Всего строк:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .supplier_total_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Закрыто актами:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .supplier_closed_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Осталось:{' '}

                            <Text strong>
                                {Number(
                                    orders
                                        .supplier_remaining_rows
                                ) || 0}
                            </Text>
                        </Text>

                        <Text>
                            Закрытая сумма:{' '}

                            <Text strong>
                                {formatMoney(
                                    orders
                                        .supplier_closed_amount
                                )}{' '}
                                ₽
                            </Text>
                        </Text>

                        <Text>
                            Полностью закрытых заказов:{' '}

                            <Tag color="green">
                                {Number(
                                    orders
                                        .supplier_orders_fully_closed
                                ) || 0}
                            </Tag>
                        </Text>
                    </Space>
                </Col>
            </Row>
        </Card>
    </Col>
</Row>

<Row
    gutter={[16, 16]}
    style={{
        marginBottom: 16
    }}
>
    <Col
        xs={24}
        xl={8}
    >
        <Card
            title="Состав документов за период"

            style={{
                height: '100%'
            }}
        >
            {documentChartData.some(
                item =>
                    item.value > 0
            ) ? (
                <div
                    style={{
                        width: '100%',
                        height: 320
                    }}
                >
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >
                        <PieChart>
                            <Pie
                                data={
                                    documentChartData
                                }

                                dataKey="value"
                                nameKey="name"

                                cx="50%"
                                cy="50%"

                                outerRadius={
                                    100
                                }

                                label={({
                                    name,
                                    value
                                }) =>
                                    `${name}: ${value}`
                                }
                            >
                                {documentChartData.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <Cell
                                            key={
                                                item.name
                                            }

                                            fill={
                                                DOCUMENT_COLORS[
                                                    index %
                                                    DOCUMENT_COLORS.length
                                                ]
                                            }
                                        />
                                    )
                                )}
                            </Pie>

                            <ChartTooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <Alert
                    type="info"
                    showIcon
                    message="За выбранный период документы отсутствуют"
                />
            )}
        </Card>
    </Col>

    <Col
        xs={24}
        xl={16}
    >
        <Card
            title="Сводка по суммам документов"

            style={{
                height: '100%'
            }}
        >
            <Row
                gutter={[16, 16]}
            >
                <Col
                    xs={24}
                    sm={12}
                >
                    <Statistic
                        title="Заказы покупателя"

                        value={
                            Number(
                                orders
                                    .customer_orders_amount
                            ) || 0
                        }

                        formatter={
                            value =>
                                formatMoney(
                                    value
                                )
                        }

                        suffix="₽"
                    />
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Statistic
                        title="Заказы поставщика"

                        value={
                            Number(
                                orders
                                    .supplier_orders_amount
                            ) || 0
                        }

                        formatter={
                            value =>
                                formatMoney(
                                    value
                                )
                        }

                        suffix="₽"
                    />
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Statistic
                        title="Акты покупателя"

                        value={
                            Number(
                                orders
                                    .customer_acts_amount
                            ) || 0
                        }

                        formatter={
                            value =>
                                formatMoney(
                                    value
                                )
                        }

                        suffix="₽"
                    />
                </Col>

                <Col
                    xs={24}
                    sm={12}
                >
                    <Statistic
                        title="Акты поставщика"

                        value={
                            Number(
                                orders
                                    .supplier_acts_amount
                            ) || 0
                        }

                        formatter={
                            value =>
                                formatMoney(
                                    value
                                )
                        }

                        suffix="₽"
                    />
                </Col>
            </Row>
        </Card>
    </Col>
</Row>

                        {/* KPI */}

                        <Row
                            gutter={[
                                16,
                                16
                            ]}

                            style={{
                                marginBottom:
                                    16
                            }}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Всего заявок"

                                        value={
                                            Number(
                                                summary.total_tasks
                                            ) ||
                                            0
                                        }

                                        prefix={
                                            <ProjectOutlined />
                                        }
                                    />
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Закрыто заявок"

                                        value={
                                            Number(
                                                summary.closed_tasks
                                            ) ||
                                            0
                                        }

                                        prefix={
                                            <CheckCircleOutlined />
                                        }
                                    />

                                    <Progress
                                        percent={
                                            closurePercent
                                        }

                                        size="small"

                                        status={
                                            closurePercent >=
                                            100
                                                ? 'success'
                                                : 'active'
                                        }

                                        style={{
                                            marginTop:
                                                12
                                        }}
                                    />
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Открыто заявок"

                                        value={
                                            Number(
                                                summary.open_tasks
                                            ) ||
                                            0
                                        }

                                        prefix={
                                            <ClockCircleOutlined />
                                        }
                                    />
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Без ВДР"

                                        value={
                                            Number(
                                                summary.tasks_without_vdr
                                            ) ||
                                            0
                                        }

                                        prefix={
                                            <CalculatorOutlined />
                                        }
                                    />

                                    <Text
                                        type="secondary"

                                        style={{
                                            display:
                                                'block',

                                            marginTop:
                                                10
                                        }}
                                    >
                                        С ВДР:{' '}

                                        <Text strong>
                                            {Number(
                                                summary.tasks_with_vdr
                                            ) ||
                                                0}
                                        </Text>
                                    </Text>
                                </Card>
                            </Col>
                        </Row>

                        {/* Графики */}

                        <Row
                            gutter={[
                                16,
                                16
                            ]}

                            style={{
                                marginBottom:
                                    16
                            }}
                        >
                            <Col
                                xs={24}
                                xl={16}
                            >
                                <Card
    title={
        `Динамика заявок: ` +
        `${statisticsPeriod[0].format(
            'DD.MM.YYYY'
        )} — ` +
        `${statisticsPeriod[1].format(
            'DD.MM.YYYY'
        )}`
    }

    extra={
        <Space wrap>
            <Tag color="blue">
                По дате создания
            </Tag>

            {exitPeriod && (
                <Tag color="purple">
                    Фильтр покидания
                </Tag>
            )}
        </Space>
    }

    style={{
        height:
            '100%'
    }}
>
    {taskStageDynamics.length >
        0 &&
    taskStages.length > 0 ? (
        <div
            style={{
                width:
                    '100%',

                height:
                    380
            }}
        >
            <ResponsiveContainer
                width="100%"
                height="100%"
            >
                <BarChart
                    data={
                        taskStageDynamics
                    }

                    margin={{
                        top:
                            10,

                        right:
                            20,

                        left:
                            0,

                        bottom:
                            10
                    }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={
                            false
                        }
                    />

                    <XAxis
                        dataKey="date"
                        interval={0}
                    />

                    <YAxis
                        allowDecimals={
                            false
                        }
                    />

                    <ChartTooltip />

                    <Legend />

                    {taskStages.map(
                        (
                            stage,
                            index
                        ) => (
                            <Bar
                                key={
                                    stage
                                }

                                dataKey={
                                    stage
                                }

                                name={
                                    stage
                                }

                                stackId="tasks"

                                fill={
                                    TASK_STAGE_COLORS[
                                        index %
                                        TASK_STAGE_COLORS
                                            .length
                                    ]
                                }

                                maxBarSize={
                                    100
                                }
                            />
                        )
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    ) : (
        <Alert
            type="info"
            showIcon
            message={
                'Нет заявок за выбранный период'
            }
        />
    )}
</Card>
                            </Col>

                            <Col
                                xs={24}
                                xl={8}
                            >
                                <Card
                                    title="Состояние осмечивания"

                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    {estimateChartData.some(
                                        item =>
                                            item.value >
                                            0
                                    ) ? (
                                        <div
                                            style={{
                                                width:
                                                    '100%',

                                                height:
                                                    340
                                            }}
                                        >
                                            <ResponsiveContainer
                                                width="100%"
                                                height="100%"
                                            >
                                                <PieChart>
                                                    <Pie
                                                        data={
                                                            estimateChartData
                                                        }

                                                        dataKey="value"

                                                        nameKey="name"

                                                        cx="50%"

                                                        cy="50%"

                                                        outerRadius={
                                                            105
                                                        }

                                                        label={({
                                                            name,
                                                            value
                                                        }) =>
                                                            `${name}: ${value}`
                                                        }
                                                    >
                                                        {estimateChartData.map(
                                                            (
                                                                item,
                                                                index
                                                            ) => (
                                                                <Cell
                                                                    key={
                                                                        item.name
                                                                    }

                                                                    fill={
                                                                        ESTIMATE_COLORS[
                                                                            index %
                                                                            ESTIMATE_COLORS.length
                                                                        ]
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </Pie>

                                                    <ChartTooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <Alert
                                            type="info"

                                            showIcon

                                            message="Нет данных по осмечиванию"
                                        />
                                    )}
                                </Card>
                            </Col>
                        </Row>

                        {/* Финансовая сводка */}

                        <Row
                            gutter={[
                                16,
                                16
                            ]}

                            style={{
                                marginBottom:
                                    16
                            }}
                        >
                            <Col
                                xs={24}
                                md={12}
                                xl={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Доходная сумма ВДР"

                                        value={
                                            Number(
                                                finance.total_income
                                            ) ||
                                            0
                                        }

                                        precision={
                                            2
                                        }

                                        suffix="₽"

                                        formatter={
                                            value =>
                                                formatMoney(
                                                    value
                                                )
                                        }

                                        prefix={
                                            <DollarCircleOutlined />
                                        }
                                    />

                                    <Text
                                        type="secondary"
                                    >
                                        Доходных строк:{' '}

                                        <Text strong>
                                            {Number(
                                                finance.income_rows
                                            ) ||
                                                0}
                                        </Text>
                                    </Text>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                                xl={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Расходная сумма ВДР"

                                        value={
                                            Number(
                                                finance.total_expense
                                            ) ||
                                            0
                                        }

                                        precision={
                                            2
                                        }

                                        suffix="₽"

                                        formatter={
                                            value =>
                                                formatMoney(
                                                    value
                                                )
                                        }

                                        prefix={
                                            <DollarCircleOutlined />
                                        }
                                    />

                                    <Text
                                        type="secondary"
                                    >
                                        Расходных строк:{' '}

                                        <Text strong>
                                            {Number(
                                                finance.expense_rows
                                            ) ||
                                                0}
                                        </Text>
                                    </Text>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                                xl={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Расчётная прибыль"

                                        value={
                                            Number(
                                                finance.profit
                                            ) ||
                                            0
                                        }

                                        precision={
                                            2
                                        }

                                        suffix="₽"

                                        formatter={
                                            value =>
                                                formatMoney(
                                                    value
                                                )
                                        }

                                        prefix={
                                            <RiseOutlined />
                                        }
                                    />

                                    <Tag
                                        color={
                                            getProfitColor(
                                                finance.profit
                                            )
                                        }

                                        style={{
                                            marginTop:
                                                10
                                        }}
                                    >
                                        {Number(
                                            finance.profit
                                        ) >= 0
                                            ? 'Прибыль'
                                            : 'Убыток'}
                                    </Tag>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                                xl={6}
                            >
                                <Card
                                    style={{
                                        height:
                                            '100%'
                                    }}
                                >
                                    <Statistic
                                        title="Рентабельность"

                                        value={
                                            Number(
                                                finance.profitability
                                            ) ||
                                            0
                                        }

                                        precision={
                                            2
                                        }

                                        suffix="%"

                                        prefix={
                                            <RiseOutlined />
                                        }
                                    />

                                    <Tag
                                        color={
                                            getProfitabilityColor(
                                                finance.profitability
                                            )
                                        }

                                        style={{
                                            marginTop:
                                                10
                                        }}
                                    >
                                        {Number(
                                            finance.profitability
                                        ) >= 20
                                            ? 'Нормальная'
                                            : Number(
                                                finance.profitability
                                            ) >= 0
                                                ? 'Низкая'
                                                : 'Отрицательная'}
                                    </Tag>
                                </Card>
                            </Col>
                        </Row>

                        <Card
                            title="Заполнение ВДР по сотрудникам"

                            style={{
                                marginBottom:
                                    24
                            }}
                        >
                            <Table
                                rowKey={record =>
                                    `${record.user_id}-${record.user_name}`
                                }

                                size="small"

                                bordered

                                scroll={{
                                    x:
                                        2650
                                }}

                                dataSource={
                                    employees
                                }

                                columns={
                                    employeeColumns
                                }

                                locale={{
                                    emptyText:
                                        'Нет данных по сотрудникам'
                                }}

                                pagination={{
                                    defaultPageSize:
                                        20,

                                    showSizeChanger:
                                        true,

                                    pageSizeOptions: [
                                        10,
                                        20,
                                        50,
                                        100
                                    ],

                                    showTotal:
                                        total =>
                                            `Сотрудников: ${total}`
                                }}

                               
                            />
                        </Card>

                        <Card
    title={
        <Space
            direction="vertical"
            size={0}
        >
            <span>
                Статистика по заказчикам
            </span>

            <Text
                type="secondary"
                style={{
                    fontSize: 12
                }}
            >
                Заявки, осмечивание, заказы,
                акты и расходы за выбранный период
            </Text>
        </Space>
    }

    style={{
        marginBottom: 16
    }}
>
    <Table
        rowKey={
            record =>
                record.customer_name
        }

        size="small"

        bordered

        dataSource={
            customerStatistics
        }

        columns={
            customerColumns
        }

        scroll={{
            x: 2750
        }}

        locale={{
            emptyText:
                'За выбранный период заявки заказчиков отсутствуют'
        }}

        pagination={{
            defaultPageSize:
                20,

            showSizeChanger:
                true,

            pageSizeOptions: [
                10,
                20,
                50,
                100
            ],

            showTotal:
                total =>
                    `Заказчиков: ${total}`
        }}
    />
</Card>

<Card
    title={
        <Space
            direction="vertical"
            size={0}
        >
            <span>
                Заявки по исполнителям
                и этапам
            </span>

            <Text
                type="secondary"
                style={{
                    fontSize:
                        12
                }}
            >
                Сколько заявок находится
                у каждого исполнителя
                на каждом этапе
            </Text>
        </Space>
    }

    style={{
        marginBottom:
            24
    }}
>
    <Table
        rowKey={
            record =>
                record.executor_name
        }

        size="small"

        bordered

        dataSource={
            executorStageStatistics
        }

        columns={
            executorStageColumns
        }

        scroll={{
            x:
                385 +
                taskStages.length *
                155
        }}

        locale={{
            emptyText:
                'Нет заявок по исполнителям за выбранный период'
        }}

        pagination={{
            defaultPageSize:
                20,

            showSizeChanger:
                true,

            pageSizeOptions: [
                10,
                20,
                50,
                100
            ],

            showTotal:
                total =>
                    `Исполнителей: ${total}`
        }}
    />
</Card>

                                </Spin>
        )}
    </>
)}

                {canSeeEstimateRanking && (
                    <div style={{ marginTop: isAdmin ? 0 : 50 }}>
                        {/* Рейтинг осмечивания */}

                        <Card
                            title={
                                isAdmin
                                    ? 'Кто сколько осметил заявок'
                                    : 'Мои осмеченные и отредактированные заявки'
                            }
                            style={{
                                marginBottom: 24
                            }}
                            extra={
                                <Text type="secondary">
                                    {isAdmin
                                        ? 'Правки без дня первого осмечивания ВДР'
                                        : 'Только ваши данные · правки без дня первого осмечивания'}
                                </Text>
                            }
                        >
                            <Space
                                wrap
                                style={{
                                    marginBottom: 16,
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Space wrap>
                                    <RangePicker
                                        value={estimateRankingPeriod}
                                        allowClear={false}
                                        format="DD.MM.YYYY"
                                        onChange={value => {
                                            if (
                                                !value?.[0] ||
                                                !value?.[1]
                                            ) {
                                                return;
                                            }

                                            setEstimateRankingPeriod(
                                                value
                                            );
                                            fetchEstimateRanking(
                                                value
                                            );
                                        }}
                                        presets={[
                                            {
                                                label: 'День',
                                                value: [
                                                    dayjs().startOf(
                                                        'day'
                                                    ),
                                                    dayjs().endOf(
                                                        'day'
                                                    )
                                                ]
                                            },
                                            {
                                                label: 'Неделя',
                                                value: [
                                                    dayjs().startOf(
                                                        'isoWeek'
                                                    ),
                                                    dayjs().endOf(
                                                        'isoWeek'
                                                    )
                                                ]
                                            },
                                            {
                                                label: 'Месяц',
                                                value: [
                                                    dayjs().startOf(
                                                        'month'
                                                    ),
                                                    dayjs().endOf(
                                                        'month'
                                                    )
                                                ]
                                            }
                                        ]}
                                    />
                                    <Button
                                        icon={
                                            <ReloadOutlined />
                                        }
                                        loading={
                                            estimateRankingLoading
                                        }
                                        onClick={() =>
                                            fetchEstimateRanking(
                                                estimateRankingPeriod
                                            )
                                        }
                                    >
                                        Обновить
                                    </Button>
                                </Space>
                            </Space>

                            <Table
                                size="small"
                                loading={
                                    estimateRankingLoading
                                }
                                rowKey={row =>
                                    `est-${row.user_id}-${row.user_name}`
                                }
                                pagination={false}
                                dataSource={
                                    estimateRankingRows
                                }
                                locale={{
                                    emptyText:
                                        'Нет осмеченных заявок за период'
                                }}
                                columns={[
                                    {
                                        title: 'Сотрудник',
                                        dataIndex: 'user_name',
                                        render: value => (
                                            <Space>
                                                <Avatar
                                                    size="small"
                                                    icon={
                                                        <UserOutlined />
                                                    }
                                                />
                                                {value || '—'}
                                            </Space>
                                        )
                                    },
                                    {
                                        title: 'Осмечено',
                                        dataIndex:
                                            'estimated_count',
                                        width: 110,
                                        align: 'right',
                                        sorter: (a, b) =>
                                            Number(
                                                a.estimated_count
                                            ) -
                                            Number(
                                                b.estimated_count
                                            ),
                                        defaultSortOrder:
                                            'descend'
                                    },
                                    {
                                        title: 'Отредактировано заявок',
                                        dataIndex:
                                            'edited_count',
                                        width: 160,
                                        align: 'right',
                                        sorter: (a, b) =>
                                            Number(
                                                a.edited_count
                                            ) -
                                            Number(
                                                b.edited_count
                                            )
                                    },
                                    {
                                        title: 'Строк',
                                        dataIndex:
                                            'edit_actions_count',
                                        width: 90,
                                        align: 'right'
                                    },
                                    {
                                        title: 'Заявки периода',
                                        dataIndex:
                                            'period_tasks_count',
                                        width: 130,
                                        align: 'right'
                                    },
                                    {
                                        title: 'Прошлые',
                                        dataIndex:
                                            'previous_tasks_count',
                                        width: 100,
                                        align: 'right'
                                    }
                                ]}
                                summary={pageData => {
                                    const total = pageData.reduce(
                                        (sum, row) =>
                                            sum +
                                            (Number(
                                                row.estimated_count
                                            ) || 0),
                                        0
                                    );
                                    const editedTotal =
                                        pageData.reduce(
                                            (sum, row) =>
                                                sum +
                                                (Number(
                                                    row.edited_count
                                                ) || 0),
                                            0
                                        );
                                    const editActionsTotal =
                                        pageData.reduce(
                                            (sum, row) =>
                                                sum +
                                                (Number(
                                                    row.edit_actions_count
                                                ) || 0),
                                            0
                                        );
                                    const periodTotal =
                                        pageData.reduce(
                                            (sum, row) =>
                                                sum +
                                                (Number(
                                                    row.period_tasks_count
                                                ) || 0),
                                            0
                                        );
                                    const previousTotal =
                                        pageData.reduce(
                                            (sum, row) =>
                                                sum +
                                                (Number(
                                                    row.previous_tasks_count
                                                ) || 0),
                                            0
                                        );

                                    return (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}>
                                                <Text strong>
                                                    Итого
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell
                                                index={1}
                                                align="right"
                                            >
                                                <Text strong>
                                                    {total}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell
                                                index={2}
                                                align="right"
                                            >
                                                <Text strong>
                                                    {
                                                        editedTotal
                                                    }
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell
                                                index={3}
                                                align="right"
                                            >
                                                <Text strong>
                                                    {
                                                        editActionsTotal
                                                    }
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell
                                                index={4}
                                                align="right"
                                            >
                                                <Text strong>
                                                    {
                                                        periodTotal
                                                    }
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell
                                                index={5}
                                                align="right"
                                            >
                                                <Text strong>
                                                    {
                                                        previousTotal
                                                    }
                                                </Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    );
                                }}
                            />

                            {isManager && (
                                <>
                                    <Text
                                        strong
                                        style={{
                                            display:
                                                'block',
                                            marginTop: 24,
                                            marginBottom: 12
                                        }}
                                    >
                                        По заказчикам
                                        (регионам)
                                    </Text>
                                    <Table
                                        size="small"
                                        loading={
                                            estimateRankingLoading
                                        }
                                        rowKey={row =>
                                            `cust-${row.customer_name}`
                                        }
                                        pagination={
                                            false
                                        }
                                        dataSource={
                                            estimateRankingByCustomer
                                        }
                                        locale={{
                                            emptyText:
                                                'Нет данных по заказчикам за период'
                                        }}
                                        columns={[
                                            {
                                                title: 'Заказчик',
                                                dataIndex:
                                                    'customer_name',
                                                render: value =>
                                                    value ||
                                                    'Без заказчика'
                                            },
                                            {
                                                title: 'Осмечено',
                                                dataIndex:
                                                    'estimated_count',
                                                width: 110,
                                                align: 'right',
                                                sorter: (
                                                    a,
                                                    b
                                                ) =>
                                                    Number(
                                                        a.estimated_count
                                                    ) -
                                                    Number(
                                                        b.estimated_count
                                                    ),
                                                defaultSortOrder:
                                                    'descend'
                                            },
                                            {
                                                title: 'Отредактировано заявок',
                                                dataIndex:
                                                    'edited_count',
                                                width: 160,
                                                align: 'right',
                                                sorter: (
                                                    a,
                                                    b
                                                ) =>
                                                    Number(
                                                        a.edited_count
                                                    ) -
                                                    Number(
                                                        b.edited_count
                                                    )
                                            },
                                            {
                                                title: 'Строк',
                                                dataIndex:
                                                    'edit_actions_count',
                                                width: 90,
                                                align: 'right'
                                            },
                                            {
                                                title: 'Заявки периода',
                                                dataIndex:
                                                    'period_tasks_count',
                                                width: 130,
                                                align: 'right'
                                            },
                                            {
                                                title: 'Прошлые',
                                                dataIndex:
                                                    'previous_tasks_count',
                                                width: 100,
                                                align: 'right'
                                            }
                                        ]}
                                        summary={pageData => {
                                            const total =
                                                pageData.reduce(
                                                    (
                                                        sum,
                                                        row
                                                    ) =>
                                                        sum +
                                                        (Number(
                                                            row.estimated_count
                                                        ) ||
                                                            0),
                                                    0
                                                );
                                            const editedTotal =
                                                pageData.reduce(
                                                    (
                                                        sum,
                                                        row
                                                    ) =>
                                                        sum +
                                                        (Number(
                                                            row.edited_count
                                                        ) ||
                                                            0),
                                                    0
                                                );
                                            const editActionsTotal =
                                                pageData.reduce(
                                                    (
                                                        sum,
                                                        row
                                                    ) =>
                                                        sum +
                                                        (Number(
                                                            row.edit_actions_count
                                                        ) ||
                                                            0),
                                                    0
                                                );
                                            const periodTotal =
                                                pageData.reduce(
                                                    (
                                                        sum,
                                                        row
                                                    ) =>
                                                        sum +
                                                        (Number(
                                                            row.period_tasks_count
                                                        ) ||
                                                            0),
                                                    0
                                                );
                                            const previousTotal =
                                                pageData.reduce(
                                                    (
                                                        sum,
                                                        row
                                                    ) =>
                                                        sum +
                                                        (Number(
                                                            row.previous_tasks_count
                                                        ) ||
                                                            0),
                                                    0
                                                );

                                            return (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0}>
                                                        <Text strong>
                                                            Итого
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell
                                                        index={1}
                                                        align="right"
                                                    >
                                                        <Text strong>
                                                            {
                                                                total
                                                            }
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell
                                                        index={2}
                                                        align="right"
                                                    >
                                                        <Text strong>
                                                            {
                                                                editedTotal
                                                            }
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell
                                                        index={3}
                                                        align="right"
                                                    >
                                                        <Text strong>
                                                            {
                                                                editActionsTotal
                                                            }
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell
                                                        index={4}
                                                        align="right"
                                                    >
                                                        <Text strong>
                                                            {
                                                                periodTotal
                                                            }
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell
                                                        index={5}
                                                        align="right"
                                                    >
                                                        <Text strong>
                                                            {
                                                                previousTotal
                                                            }
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            );
                                        }}
                                    />
                                </>
                            )}
                        </Card>

                    </div>
                )}

                
            </Content>
        </Layout>
    );
};

export default Dashboard;